import { lerpColor } from '../../color';
import type { World as Look } from '../../world';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, sketchLine, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;

interface Layout {
  readonly portrait: boolean;
  readonly horizon: number;
  readonly moon: Spot;
  readonly moonR: number;
  readonly deck: number;
  readonly pierEnd: number;
  readonly s: number;
}

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  const s = Math.max(0.55, Math.min(1.4, p.unit / 360));
  if (portrait) {
    const deck = Math.min(0.86 * p.h, Math.max(0.79 * p.h, p.cy + p.unit * 0.98));
    return {
      portrait,
      horizon: Math.min(0.7 * p.h, deck - 0.09 * p.h),
      moon: { x: 0.2 * p.w, y: 0.25 * p.h },
      moonR: Math.max(13, p.unit * 0.085),
      deck,
      pierEnd: 0.44 * p.w,
      s,
    };
  }
  return {
    portrait,
    horizon: 0.63 * p.h,
    moon: { x: 0.86 * p.w, y: 0.3 * p.h },
    moonR: Math.max(16, p.unit * 0.075),
    deck: 0.79 * p.h,
    pierEnd: Math.max(0.12 * p.w, Math.min(0.22 * p.w, p.cx - p.unit * 1.02)),
    s,
  };
}

function pick(p: Painter, n: number, x0: number, x1: number, y0: number, y1: number, keep: number, gap: number, avoid: readonly Spot[] = []): Spot[] {
  const out: Spot[] = [];
  for (let tries = 0; out.length < n && tries < n * 40; tries++) {
    const x = x0 + p.rng.float() * (x1 - x0);
    const y = y0 + p.rng.float() * (y1 - y0);
    if (calmAt(p, x, y, keep)) continue;
    if ([...out, ...avoid].some((o) => Math.hypot(o.x - x, o.y - y) < gap)) continue;
    out.push({ x, y });
  }
  return out;
}

function sky(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const { bg, spot } = look.palette;
  const far = lerpColor(bg, look.ceil, 0.33);
  const g = ctx.createLinearGradient(0, 0, 0, l.horizon);
  g.addColorStop(0, rgba(lerpColor(bg, look.palette.ink, 0.35)));
  g.addColorStop(0.45, rgba(bg));
  g.addColorStop(0.82, rgba(far));
  g.addColorStop(1, rgba(lerpColor(lerpColor(far, look.ceil, 0.55), spot, 0.1)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, l.horizon + 2);
}

function stars(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const n = l.portrait ? 30 : 55;
  for (let i = 0; i < n; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * (l.horizon - 0.12 * p.h);
    if (calmAt(p, x, y, 0.9) || Math.hypot(x - l.moon.x, y - l.moon.y) < l.moonR * 2.5) continue;
    ctx.fillStyle = rgba(look.palette.rim, p.rng.range(0.12, 0.42) * (y < 0.2 * p.h ? 0.7 : 1));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.5, 1.3), 0, TAU);
    ctx.fill();
  }
}

function moon(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const { x, y } = l.moon;
  const r = l.moonR;
  glowDot(ctx, x, y, r * 4, look.palette.heldLight, 0.13);
  glowDot(ctx, x, y, r * 1.9, look.palette.rim, 0.12);
  ctx.fillStyle = rgba(lerpColor(look.palette.rim, look.palette.heldLight, 0.4), 0.6);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.arc(x + r * 0.42, y - r * 0.16, r * 0.9, 0, TAU, true);
  ctx.fill('evenodd');
}

function ridge(p: Painter, base: number, height: number, color: number, waves: number): void {
  const { ctx } = p;
  const ph = [p.rng.float() * TAU, p.rng.float() * TAU, p.rng.float() * TAU];
  ctx.fillStyle = rgba(color);
  ctx.beginPath();
  ctx.moveTo(-10, base + 2);
  for (let x = -10; x <= p.w + 10; x += 8) {
    const t = x / p.w;
    const k = 0.55 + 0.25 * Math.sin(t * TAU * waves + ph[0]!) + 0.14 * Math.sin(t * TAU * waves * 2.3 + ph[1]!) + 0.06 * Math.sin(t * TAU * 7 + ph[2]!);
    ctx.lineTo(x, base - height * k);
  }
  ctx.lineTo(p.w + 10, base + 2);
  ctx.closePath();
  ctx.fill();
}

function hills(p: Painter, look: Look, l: Layout): void {
  const far = lerpColor(look.palette.bg, look.ceil, 0.33);
  ridge(p, l.horizon, p.h * 0.085, lerpColor(far, look.ceil, 0.18), 1.4);
  ridge(p, l.horizon, p.h * 0.04, lerpColor(look.palette.bg, far, 0.45), 2.6);
  const { ctx } = p;
  ctx.strokeStyle = rgba(lerpColor(look.palette.bg, look.palette.ink, 0.5), 0.85);
  ctx.lineCap = 'round';
  for (let x = p.rng.range(0, 20); x < p.w; x += p.rng.range(6, 26) * l.s) {
    if (p.rng.chance(0.35)) continue;
    const tall = p.h * p.rng.range(0.012, 0.03);
    ctx.lineWidth = p.rng.range(0.8, 1.6);
    sketchLine(ctx, x, l.horizon + 1, x + p.rng.range(-3, 3), l.horizon - tall, p.rng, 0.15);
  }
}

function lake(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const { bg, ink, rim, path } = look.palette;
  const far = lerpColor(bg, look.ceil, 0.33);
  const g = ctx.createLinearGradient(0, l.horizon, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(far, look.ceil, 0.35)));
  g.addColorStop(0.35, rgba(far));
  g.addColorStop(1, rgba(lerpColor(bg, ink, 0.3)));
  ctx.fillStyle = g;
  ctx.fillRect(0, l.horizon, p.w, p.h - l.horizon);
  ctx.strokeStyle = rgba(lerpColor(path, rim, 0.3), 0.18);
  ctx.lineWidth = 1.2;
  for (let x = 0; x < p.w; ) {
    const len = p.rng.range(20, 90) * l.s;
    if (p.rng.chance(0.6) && !calmAt(p, x + len / 2, l.horizon, 0.8)) sketchLine(ctx, x, l.horizon + 1.5, x + len, l.horizon + 1.5, p.rng, 0.01);
    x += len + p.rng.range(10, 60) * l.s;
  }
  const n = l.portrait ? 6 : 10;
  for (const at of pick(p, n, 0, p.w, l.horizon + 0.03 * p.h, p.h * 0.92, 0.95, 40 * l.s)) {
    const len = p.rng.range(14, 44) * l.s;
    ctx.strokeStyle = rgba(lerpColor(path, rim, 0.3), p.rng.range(0.1, 0.22));
    ctx.lineWidth = p.rng.range(0.9, 1.6);
    sketchLine(ctx, at.x - len / 2, at.y, at.x + len / 2, at.y, p.rng, 0.03);
  }
}

function moonPath(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const color = lerpColor(look.palette.rim, look.palette.heldLight, 0.35);
  let y = l.horizon + 0.012 * p.h;
  for (let i = 0; i < 8 && y < p.h * 0.9; i++) {
    const half = l.moonR * p.rng.range(0.5, 1.5) * (1 - i * 0.06);
    const dx = p.rng.range(-0.4, 0.4) * l.moonR;
    ctx.strokeStyle = rgba(color, p.rng.range(0.16, 0.28) * (1 - i * 0.07));
    ctx.lineWidth = p.rng.range(1.2, 2.6) * l.s;
    ctx.lineCap = 'round';
    sketchLine(ctx, l.moon.x + dx - half, y, l.moon.x + dx + half, y, p.rng, 0.02);
    y += p.h * p.rng.range(0.018, 0.04);
  }
}

function paperLantern(p: Painter, x: number, y: number, size: number, color: number, alpha: number, look: Look): void {
  const { ctx } = p;
  glowDot(ctx, x, y, size * 3.2, color, alpha * 0.35);
  ctx.fillStyle = rgba(color, alpha);
  ctx.beginPath();
  ctx.ellipse(x, y, size * 0.72, size, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(look.palette.heldLight, alpha * 0.8);
  ctx.beginPath();
  ctx.ellipse(x, y + size * 0.2, size * 0.3, size * 0.42, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(color, look.palette.ink, 0.6), alpha);
  ctx.fillRect(x - size * 0.38, y - size * 1.04, size * 0.76, size * 0.18);
  ctx.fillRect(x - size * 0.32, y + size * 0.88, size * 0.64, size * 0.16);
}

function risers(p: Painter, look: Look, l: Layout, count: number, min: number, max: number, alpha: number): void {
  const top = 0.13 * p.h;
  const bottom = l.horizon - 0.08 * p.h;
  for (const at of pick(p, count, 0, p.w, top, bottom, 1.0, 34 * l.s, [l.moon])) {
    if (Math.hypot(at.x - l.moon.x, at.y - l.moon.y) < l.moonR * 2.6) continue;
    const size = p.rng.range(min, max) * l.s;
    const color = p.rng.chance(0.2) ? look.palette.spot : look.palette.held;
    paperLantern(p, at.x, at.y, size, color, alpha * p.rng.range(0.75, 1), look);
  }
}

function floaters(p: Painter, look: Look, l: Layout, count: number): void {
  const { ctx } = p;
  for (const at of pick(p, count, 0, p.w, l.horizon + 0.04 * p.h, p.h * 0.86, 1.0, 50 * l.s)) {
    const size = p.rng.range(5, 8) * l.s;
    ctx.strokeStyle = rgba(look.palette.held, 0.22);
    ctx.lineWidth = 1.4 * l.s;
    for (let i = 1; i <= 3; i++) {
      const half = size * p.rng.range(0.6, 1.3);
      sketchLine(ctx, at.x - half, at.y + size * (0.8 + i * 0.75), at.x + half, at.y + size * (0.8 + i * 0.75), p.rng, 0.02);
    }
    paperLantern(p, at.x, at.y, size, look.palette.held, 0.55, look);
  }
}

function pier(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const { bg, ink, held, heldLight } = look.palette;
  const wood = lerpColor(lerpColor(bg, look.ceil, 0.6), held, 0.06);
  const dark = lerpColor(bg, ink, 0.35);
  const line = lerpColor(ink, bg, 0.2);
  const thick = Math.max(6, p.h * 0.016);
  const x0 = -12;
  const x1 = l.pierEnd;
  const step = Math.max(26, (x1 - x0) / 5);
  ctx.lineCap = 'round';
  for (let x = x1 - 4; x > x0; x -= step * p.rng.range(0.85, 1.15)) {
    const depth = p.h * p.rng.range(0.05, 0.075);
    const pw = Math.max(4, thick * 0.6);
    ctx.fillStyle = rgba(dark);
    ctx.fillRect(x - pw / 2, l.deck, pw, depth);
    ctx.strokeStyle = rgba(dark, 0.45);
    ctx.lineWidth = pw * 0.8;
    for (let k = 0; k < 3; k++) {
      const y = l.deck + depth + 3 + k * pw * 1.3;
      sketchLine(ctx, x - pw * (0.6 - k * 0.15), y, x + pw * (0.5 - k * 0.1), y, p.rng, 0.05);
    }
  }
  ctx.fillStyle = rgba(wood);
  ctx.fillRect(x0, l.deck - thick, x1 - x0, thick);
  ctx.fillStyle = rgba(dark);
  ctx.fillRect(x0, l.deck, x1 - x0, thick * 0.45);
  ctx.strokeStyle = rgba(line, 0.9);
  ctx.lineWidth = 1.6;
  sketchLine(ctx, x0, l.deck - thick, x1, l.deck - thick, p.rng, 0.004);
  sketchLine(ctx, x1, l.deck - thick, x1, l.deck + thick * 0.45, p.rng, 0.02);
  ctx.strokeStyle = rgba(line, 0.35);
  ctx.lineWidth = 1;
  for (let x = x0 + p.rng.range(20, 50); x < x1 - 10; x += p.rng.range(30, 70) * l.s) {
    sketchLine(ctx, x, l.deck - thick + 1, x + p.rng.range(-2, 2), l.deck - 1, p.rng, 0.05);
  }
  const postX = x1 - thick * 1.2;
  const postTop = l.deck - thick - p.h * 0.13;
  ctx.strokeStyle = rgba(wood);
  ctx.lineWidth = Math.max(3, thick * 0.55);
  sketchLine(ctx, postX, l.deck - thick, postX, postTop, p.rng, 0.01);
  ctx.lineWidth = Math.max(2, thick * 0.35);
  const armX = postX + p.h * 0.035;
  sketchLine(ctx, postX, postTop + 3, armX, postTop + 3, p.rng, 0.04);
  ctx.strokeStyle = rgba(line, 0.7);
  ctx.lineWidth = 1;
  sketchLine(ctx, armX, postTop + 3, armX, postTop + p.h * 0.025, p.rng, 0.02);
  const size = Math.max(6, 9 * l.s);
  paperLantern(p, armX, postTop + p.h * 0.025 + size, size, held, 0.6, look);
  ctx.strokeStyle = rgba(heldLight, 0.2);
  ctx.lineWidth = 2 * l.s;
  for (let i = 0; i < 3; i++) {
    const y = l.deck + thick + p.h * (0.02 + i * 0.022);
    const half = size * p.rng.range(0.6, 1.4);
    sketchLine(ctx, armX - half, y, armX + half, y, p.rng, 0.03);
  }
}

function reedClump(p: Painter, look: Look, x0: number, x1: number, base: number, tall: number, dir: number, l: Layout): void {
  const { ctx } = p;
  const color = lerpColor(look.palette.bg, look.palette.ink, 0.55);
  ctx.strokeStyle = rgba(color, 0.95);
  ctx.fillStyle = rgba(color, 0.95);
  ctx.lineCap = 'round';
  const n = Math.round(Math.abs(x1 - x0) / (9 * l.s)) + 6;
  for (let i = 0; i < n; i++) {
    const x = x0 + p.rng.float() * (x1 - x0);
    const t = 1 - Math.abs(x - (dir > 0 ? x0 : x1)) / Math.max(1, Math.abs(x1 - x0));
    const h = tall * p.rng.range(0.35, 1) * (0.45 + 0.55 * t);
    const lean = dir * p.rng.range(-0.05, 0.22) * h;
    const tx = x + lean;
    const ty = base - h;
    ctx.lineWidth = p.rng.range(1.2, 2.6) * l.s;
    ctx.beginPath();
    ctx.moveTo(x, base + 4);
    ctx.quadraticCurveTo(x + lean * 0.2, base - h * 0.6, tx, ty);
    ctx.stroke();
    if (p.rng.chance(0.3)) {
      ctx.beginPath();
      ctx.ellipse(x + lean * 0.75, base - h * 0.82, 2.4 * l.s, 7 * l.s, lean / h, 0, TAU);
      ctx.fill();
    }
  }
}

function reeds(p: Painter, look: Look, l: Layout): void {
  const tall = (l.portrait ? 0.1 : 0.2) * p.h;
  const base = p.h + 4;
  const reach = (l.portrait ? 0.22 : 0.13) * p.w;
  reedClump(p, look, -6, reach, base, tall, 1, l);
  reedClump(p, look, p.w - reach, p.w + 6, base, tall * 0.85, -1, l);
}

function lotus(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const { path, rim, spot, bg } = look.palette;
  const far = lerpColor(bg, look.ceil, 0.33);
  const leaf = lerpColor(far, path, 0.2);
  const y0 = l.portrait ? Math.max(l.deck + 0.03 * p.h, 0.84 * p.h) : 0.84 * p.h;
  const corners: [number, number][] = l.portrait
    ? [[0.62 * p.w, 0.96 * p.w]]
    : [[0.04 * p.w, 0.2 * p.w], [0.8 * p.w, 0.97 * p.w]];
  for (const [a, b] of corners) {
    for (const at of pick(p, 4, a, b, y0, p.h * 0.97, 1.0, 26 * l.s)) {
      const r = p.rng.range(9, 16) * l.s;
      const rot = p.rng.float() * TAU;
      ctx.fillStyle = rgba(leaf, 0.85);
      ctx.beginPath();
      ctx.moveTo(at.x, at.y);
      ctx.ellipse(at.x, at.y, r, r * 0.38, 0, rot + 0.35, rot + TAU - 0.35);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = rgba(lerpColor(leaf, rim, 0.25), 0.35);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    const bud = pick(p, 1, a, b, y0 - 0.01 * p.h, p.h * 0.93, 1.0, 1)[0];
    if (bud === undefined) continue;
    const s = 6 * l.s;
    glowDot(ctx, bud.x, bud.y - s, s * 3, spot, 0.12);
    ctx.fillStyle = rgba(spot, 0.55);
    for (const k of [-1, 0, 1]) {
      ctx.beginPath();
      ctx.ellipse(bud.x + k * s * 0.55, bud.y - s * (k === 0 ? 1.05 : 0.8), s * 0.42, s * 0.95, k * 0.5, 0, TAU);
      ctx.fill();
    }
  }
}

function garland(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const thick = Math.max(6, p.h * 0.016);
  const ax = l.pierEnd - thick * 1.2;
  const ay = l.deck - thick - p.h * 0.13;
  const bx = -8;
  const by = ay - p.h * (l.portrait ? 0.02 : 0.05);
  const sag = p.h * 0.05;
  ctx.strokeStyle = rgba(look.palette.path, 0.35);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const pt = catenary(ax, ay, bx, by, sag, i / 24);
    if (i === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  }
  ctx.stroke();
  const n = 4;
  for (let i = 1; i <= n; i++) {
    const pt = catenary(ax, ay, bx, by, sag, i / (n + 1));
    const size = 5.5 * l.s;
    paperLantern(p, pt.x, pt.y + size * 1.2, size, i % 2 === 0 ? look.palette.spot : look.palette.held, 0.5, look);
  }
}

function skyLanterns(p: Painter, look: Look, l: Layout): void {
  const { ctx } = p;
  const top = 0.12 * p.h;
  const bottom = l.horizon - 0.05 * p.h;
  for (const at of pick(p, l.portrait ? 18 : 32, 0, p.w, top, bottom, 1.0, 18 * l.s, [l.moon])) {
    if (Math.hypot(at.x - l.moon.x, at.y - l.moon.y) < l.moonR * 2.4) continue;
    const r = p.rng.range(1.4, 2.8) * l.s;
    glowDot(ctx, at.x, at.y, r * 4, look.palette.held, 0.12);
    ctx.fillStyle = rgba(look.palette.held, p.rng.range(0.28, 0.5));
    ctx.beginPath();
    ctx.ellipse(at.x, at.y, r * 0.75, r, 0, 0, TAU);
    ctx.fill();
  }
}

export function paint(p: Painter, look: Look): void {
  const l = layoutOf(p);
  sky(p, look, l);
  stars(p, look, l);
  if (p.level >= 4) skyLanterns(p, look, l);
  moon(p, look, l);
  hills(p, look, l);
  lake(p, look, l);
  moonPath(p, look, l);
  risers(p, look, l, 3, 6, 9, 0.55);
  floaters(p, look, l, 2);
  pier(p, look, l);
  if (p.level >= 3) garland(p, look, l);
  if (p.level >= 2) lotus(p, look, l);
  reeds(p, look, l);
  calmCenter(p, look.palette.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, look.palette.ink, 0.45);
}
